import { useState, useEffect, useMemo } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {GraduationCap, Play, CheckCircle2, ArrowRight, Lock, Settings, Save, Eye, EyeOff} from 'lucide-react'

// ══════════════════════════════════════════
// TIPOS
// ══════════════════════════════════════════
type VisivelPara = 'assinantes' | 'gratuito' | 'ambos'

interface Aula {
  num: number
  chave: string
  titulo: string
  /** ID do vídeo no ScreenPal (usado no player e no script de appearance). */
  spId: string
  /** Para quem a aula fica visível: assinantes (anual), gratuito ou ambos. */
  visivelPara: VisivelPara
  /** Se false, a aula não aparece para as consultoras. */
  disponivel: boolean
}

// ══════════════════════════════════════════
// LISTA PADRÃO DE AULAS (fallback)
// ══════════════════════════════════════════
const AULAS_PADRAO: Aula[] = [
  { num: 1, chave: 'aula1', titulo: 'O que é o Sistema Descubra seus Óleos', spId: 'cOifcPnUoHh', visivelPara: 'ambos', disponivel: true },
  { num: 2, chave: 'aula2', titulo: 'Como funciona o Sistema Descubra seus Óleos', spId: 'cO1DlBnugCw', visivelPara: 'ambos', disponivel: true },
  { num: 3, chave: 'aula3', titulo: 'Explicação detalhada do Diagnóstico e da Capacitação com Certificado — Parte 1', spId: 'cO1Dl1nugA9', visivelPara: 'ambos', disponivel: true },
  { num: 4, chave: 'aula4', titulo: 'Explicação detalhada do Diagnóstico e da Capacitação com Certificado — Parte 2', spId: 'cO1Dl2nuggi', visivelPara: 'ambos', disponivel: true },
  { num: 5, chave: 'aula5', titulo: 'Painel Administrativo — Como Localizar os Diagnósticos Preenchidos — Parte 1', spId: 'cO1DXsnugam', visivelPara: 'ambos', disponivel: true },
  { num: 6, chave: 'aula6', titulo: 'Painel Administrativo — Como Localizar os Diagnósticos Preenchidos — Parte 2', spId: 'cO1DXMnugaW', visivelPara: 'ambos', disponivel: true },
  { num: 7, chave: 'aula7', titulo: 'Passo a Passo do Diagnóstico de Crianças e Adolescentes', spId: 'cO1DXDnugwY', visivelPara: 'ambos', disponivel: true },
  { num: 8, chave: 'aula8', titulo: 'Como Localizar os Diagnósticos Kids Preenchidos', spId: 'cO1DXDnugw0', visivelPara: 'ambos', disponivel: true },
  { num: 9, chave: 'aula9', titulo: 'Como fazer Orçamentos de Óleos Essenciais Rapidamente', spId: 'cO1DXDnugwT', visivelPara: 'ambos', disponivel: true },
  { num: 10, chave: 'aula10', titulo: 'Editando seu Perfil e Botão de Suporte', spId: 'cO1DXDnugwO', visivelPara: 'ambos', disponivel: true },
  { num: 11, chave: 'aula11', titulo: 'Como Divulgar seus Diagnósticos', spId: 'cO1DXDnugwu', visivelPara: 'ambos', disponivel: true },
]

const CHAVE_CONFIG = 'aulas_treinamento'

// URL do player ScreenPal com share/download desativados (protege conteúdo sensível).
const montarSrcScreenPal = (spId: string) =>
  `https://go.screenpal.com/player/${spId}?ff=1&ahc=1&dcc=1&tl=1&bg=transparent&share=0&download=0&embed=1&cl=1`

/**
 * Extrai o ID do ScreenPal de uma URL colada pelo admin.
 * Aceita links do tipo /watch/<id>, /player/<id> ou o próprio id puro.
 */
const extrairSpId = (input: string): string => {
  const texto = (input || '').trim()
  if (!texto) return ''
  const match = texto.match(/(?:watch|player)\/([A-Za-z0-9]+)/)
  if (match?.[1]) return match[1]
  // Se não bateu no padrão de URL, assume que já é o id puro (sem espaços/barras).
  if (/^[A-Za-z0-9]+$/.test(texto)) return texto
  return texto
}

type ProgressoTreinamento = {
  [key: string]: boolean | number | string | null | undefined
  totalAssistidas?: number
  dataInicioTreinamento?: string | null
  dataConclusao?: string | null
}

interface AbaTreinamentoProps {
  isAdmin: boolean
  consultoraLogada: string | null
  consultoras: any[]
  /** Recarrega lista de consultoras no componente pai após salvar progresso. */
  onRecarregarConsultoras?: () => void
}

// ══════════════════════════════════════════
// HOOK: carrega/salva configuração das aulas
// ══════════════════════════════════════════
function normalizarAulas(raw: any): Aula[] {
  const base = Array.isArray(raw) ? raw : []
  // Mescla o padrão com o salvo, garantindo que novas aulas do código apareçam
  return AULAS_PADRAO.map(padrao => {
    const salvo = base.find((a: any) => a?.chave === padrao.chave)
    if (!salvo) return { ...padrao }
    return {
      num: padrao.num,
      chave: padrao.chave,
      titulo: typeof salvo.titulo === 'string' && salvo.titulo.trim() ? salvo.titulo : padrao.titulo,
      spId: typeof salvo.spId === 'string' && salvo.spId.trim() ? salvo.spId : padrao.spId,
      visivelPara: (['assinantes', 'gratuito', 'ambos'].includes(salvo.visivelPara) ? salvo.visivelPara : padrao.visivelPara) as VisivelPara,
      disponivel: typeof salvo.disponivel === 'boolean' ? salvo.disponivel : padrao.disponivel,
    }
  })
}

function useAulasConfig() {
  const [aulas, setAulas] = useState<Aula[]>(AULAS_PADRAO)
  const [docId, setDocId] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    let ativo = true
    ;(async () => {
      try {
        const { list } = await lumi.entities.configuracoes.list({
          filter: { chave: CHAVE_CONFIG },
        })
        if (!ativo) return
        const doc = list?.[0]
        if (doc) {
          setDocId(doc._id)
          let parsed: any = []
          try {
            parsed = typeof doc.valor === 'string' ? JSON.parse(doc.valor) : doc.valor
          } catch {
            parsed = []
          }
          setAulas(normalizarAulas(parsed))
        } else {
          setAulas(AULAS_PADRAO)
        }
      } catch (e) {
        console.error('[treinamento] erro ao carregar config de aulas', e)
        if (ativo) setAulas(AULAS_PADRAO)
      } finally {
        if (ativo) setCarregando(false)
      }
    })()
    return () => {
      ativo = false
    }
  }, [])

  const salvarAulas = async (novasAulas: Aula[]) => {
    const valor = JSON.stringify(novasAulas)
    const agora = new Date().toISOString()
    if (docId) {
      await lumi.entities.configuracoes.update(docId, { valor, updatedAt: agora })
    } else {
      const criado = await lumi.entities.configuracoes.create({
        chave: CHAVE_CONFIG,
        valor,
        descricao: 'Configuração das aulas da aba Como Usar o Sistema (título, link, visibilidade e status)',
        createdAt: agora,
        updatedAt: agora,
      })
      if (criado?._id) setDocId(criado._id)
    }
    setAulas(novasAulas)
  }

  return { aulas, setAulas, salvarAulas, carregando }
}

// ══════════════════════════════════════════
// BLOQUEIO DE ACESSO (sem assinatura válida)
// ══════════════════════════════════════════
function BloqueioAcesso() {
  return (
    <div className="max-w-xl mx-auto text-center py-12">
      <div className="w-16 h-16 bg-bege-medio rounded-full flex items-center justify-center mx-auto mb-5">
        <Lock className="w-8 h-8 text-[#5F5E5A]" />
      </div>
      <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-3">
        Treinamento exclusivo para assinantes
      </h2>
      <p className="text-sm text-texto-suave leading-relaxed mb-6">
        O treinamento completo está disponível para assinantes do sistema.
        Escolha seu plano e tenha acesso imediato.
      </p>
      <a
        href="/assinar"
        className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full font-medium text-white transition-all hover:opacity-90"
        style={{ backgroundColor: '#2D6A4F' }}
      >
        Ver planos
      </a>
    </div>
  )
}

// ══════════════════════════════════════════
// GERENCIAR AULAS (admin) — edição inline
// ══════════════════════════════════════════
function GerenciarAulas({
  aulas,
  onSalvarAula,
}: {
  aulas: Aula[]
  onSalvarAula: (aula: Aula) => Promise<void>
}) {
  // Estado de edição local por aula (título e link colado)
  const [edicoes, setEdicoes] = useState<Record<string, { titulo: string; link: string; visivelPara: VisivelPara; disponivel: boolean }>>({})
  const [salvandoChave, setSalvandoChave] = useState<string | null>(null)

  useEffect(() => {
    const inicial: Record<string, { titulo: string; link: string; visivelPara: VisivelPara; disponivel: boolean }> = {}
    aulas.forEach(a => {
      inicial[a.chave] = {
        titulo: a.titulo,
        link: `https://go.screenpal.com/watch/${a.spId}`,
        visivelPara: a.visivelPara,
        disponivel: a.disponivel,
      }
    })
    setEdicoes(inicial)
  }, [aulas])

  const atualizarCampo = (chave: string, campo: 'titulo' | 'link' | 'visivelPara' | 'disponivel', valor: any) => {
    setEdicoes(prev => ({
      ...prev,
      [chave]: { ...prev[chave], [campo]: valor },
    }))
  }

  const salvar = async (aula: Aula) => {
    const ed = edicoes[aula.chave]
    if (!ed) return
    const novoSpId = extrairSpId(ed.link)
    if (!novoSpId) {
      toast.error('Informe um link válido do ScreenPal.')
      return
    }
    if (!ed.titulo.trim()) {
      toast.error('O título da aula não pode ficar vazio.')
      return
    }
    setSalvandoChave(aula.chave)
    try {
      await onSalvarAula({
        ...aula,
        titulo: ed.titulo.trim(),
        spId: novoSpId,
        visivelPara: ed.visivelPara,
        disponivel: ed.disponivel,
      })
      toast.success(`Aula ${aula.num} salva com sucesso!`)
    } catch (e) {
      console.error('[treinamento] erro ao salvar aula', e)
      toast.error('Erro ao salvar a aula. Tente novamente.')
    } finally {
      setSalvandoChave(null)
    }
  }

  return (
    <div className="mb-10">
      <div className="mb-4">
        <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-1 flex items-center gap-2">
          <Settings className="w-6 h-6 text-verde-escuro" />
          Gerenciar Aulas do Treinamento
        </h2>
        <p className="text-sm text-texto-suave">
          Edite título, link do vídeo, visibilidade e status de cada aula. As alterações ficam disponíveis imediatamente para as consultoras.
        </p>
      </div>

      <div className="space-y-4">
        {aulas.map(aula => {
          const ed = edicoes[aula.chave] || { titulo: aula.titulo, link: '', visivelPara: aula.visivelPara, disponivel: aula.disponivel }
          const salvando = salvandoChave === aula.chave
          return (
            <div key={aula.chave} className="bg-white rounded-xl shadow-md p-4 sm:p-5">
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm"
                  style={{ backgroundColor: '#F8F4EF', color: '#5F5E5A' }}
                >
                  {aula.num}
                </div>
                <div className="flex-1 min-w-0 space-y-3">
                  {/* Título */}
                  <div>
                    <label className="block text-xs font-medium text-texto-suave mb-1">Título da aula</label>
                    <input
                      type="text"
                      value={ed.titulo}
                      onChange={e => atualizarCampo(aula.chave, 'titulo', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-bege-medio text-sm text-texto-escuro focus:outline-none focus:ring-2 focus:ring-[#A8DFC4]"
                    />
                  </div>

                  {/* Link */}
                  <div>
                    <label className="block text-xs font-medium text-texto-suave mb-1">Link do vídeo no ScreenPal</label>
                    <input
                      type="text"
                      value={ed.link}
                      onChange={e => atualizarCampo(aula.chave, 'link', e.target.value)}
                      placeholder="https://go.screenpal.com/watch/..."
                      className="w-full px-3 py-2 rounded-lg border border-bege-medio text-sm text-texto-escuro focus:outline-none focus:ring-2 focus:ring-[#A8DFC4]"
                    />
                  </div>

                  <div className="flex flex-wrap items-end gap-4">
                    {/* Disponível para */}
                    <div>
                      <label className="block text-xs font-medium text-texto-suave mb-1">Disponível para</label>
                      <select
                        value={ed.visivelPara}
                        onChange={e => atualizarCampo(aula.chave, 'visivelPara', e.target.value as VisivelPara)}
                        className="px-3 py-2 rounded-lg border border-bege-medio text-sm text-texto-escuro bg-white focus:outline-none focus:ring-2 focus:ring-[#A8DFC4]"
                      >
                        <option value="assinantes">Assinantes (Anual)</option>
                        <option value="gratuito">Gratuito</option>
                        <option value="ambos">Ambos</option>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-xs font-medium text-texto-suave mb-1">Status</label>
                      <button
                        type="button"
                        onClick={() => atualizarCampo(aula.chave, 'disponivel', !ed.disponivel)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                        style={
                          ed.disponivel
                            ? { backgroundColor: '#D8F3DC', color: '#2D6A4F' }
                            : { backgroundColor: '#FEE2E2', color: '#991B1B' }
                        }
                      >
                        {ed.disponivel ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        {ed.disponivel ? 'Disponível' : 'Indisponível'}
                      </button>
                    </div>

                    {/* Salvar */}
                    <div className="ml-auto">
                      <button
                        type="button"
                        onClick={() => salvar(aula)}
                        disabled={salvando}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-medium text-white text-sm transition-all hover:opacity-90 disabled:opacity-60"
                        style={{ backgroundColor: '#2D6A4F' }}
                      >
                        <Save className="w-4 h-4" />
                        {salvando ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════
// VISÃO ADMIN — gerenciamento + progresso
// ══════════════════════════════════════════
function VisaoAdminTreinamento({
  consultoras,
  aulas,
  onSalvarAula,
}: {
  consultoras: any[]
  aulas: Aula[]
  onSalvarAula: (aula: Aula) => Promise<void>
}) {
  const totalAulas = aulas.length
  const formatarData = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('pt-BR') : '—'

  const linhas = useMemo(() => {
    return consultoras
      .map(c => {
        const prog: ProgressoTreinamento = c.progressoTreinamento || {}
        const total = aulas.filter(a => prog[a.chave]).length
        return {
          id: c._id,
          nome: c.nome || c.usuario,
          total,
          inicio: prog.dataInicioTreinamento || null,
          conclusao: total >= totalAulas ? (prog.dataConclusao || null) : null,
          concluiu: totalAulas > 0 && total >= totalAulas,
          comecou: total > 0,
        }
      })
      .sort((a, b) => b.total - a.total)
  }, [consultoras, aulas, totalAulas])

  const totalConcluiram = linhas.filter(l => l.concluiu).length
  const totalEmAndamento = linhas.filter(l => l.comecou && !l.concluiu).length
  const totalNaoComecou = linhas.filter(l => !l.comecou).length

  return (
    <div>
      {/* Seção de gerenciamento das aulas (acima da tabela de progresso) */}
      <GerenciarAulas aulas={aulas} onSalvarAula={onSalvarAula} />

      {/* Separador visual */}
      <div className="border-t-2 mb-8" style={{ borderColor: '#A8DFC4' }} />

      <div className="mb-6">
        <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-1 flex items-center gap-2">
          <GraduationCap className="w-6 h-6 text-verde-escuro" />
          Progresso das Consultoras
        </h2>
        <p className="text-sm text-texto-suave">
          Acompanhe quem assistiu às aulas do treinamento do sistema.
        </p>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total de consultoras', valor: linhas.length, cor: 'text-texto-escuro' },
          { label: 'Concluíram', valor: totalConcluiram, cor: 'text-[#2D6A4F]' },
          { label: 'Em andamento', valor: totalEmAndamento, cor: 'text-blue-600' },
          { label: 'Não começaram', valor: totalNaoComecou, cor: 'text-gray-500' },
        ].map(m => (
          <div key={m.label} className="bg-white p-5 rounded-xl shadow-md">
            <div className={`text-3xl font-bold mb-1 ${m.cor}`}>{m.valor}</div>
            <div className="text-xs text-texto-suave">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {linhas.length === 0 ? (
          <div className="p-8 text-center text-texto-suave">Nenhuma consultora cadastrada ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-bege-fundo">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Consultora</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Progresso</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Início</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Conclusão</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bege-medio">
                {linhas.map(l => (
                  <tr key={l.id} className="hover:bg-bege-fundo">
                    <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{l.nome}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-28 h-2 rounded-full bg-bege-medio overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${totalAulas > 0 ? (l.total / totalAulas) * 100 : 0}%`, backgroundColor: '#2D6A4F' }}
                          />
                        </div>
                        <span className="text-xs text-texto-suave whitespace-nowrap">{l.total} de {totalAulas}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {l.concluiu ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#D8F3DC', color: '#2D6A4F' }}>Concluído</span>
                      ) : l.comecou ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#E6F1FB] text-[#185FA5]">Em andamento</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#4B5563]">Não começou</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-texto-suave whitespace-nowrap">{formatarData(l.inicio)}</td>
                    <td className="px-4 py-3 text-sm text-texto-suave whitespace-nowrap">{formatarData(l.conclusao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════
export default function AbaTreinamento({
  isAdmin,
  consultoraLogada,
  consultoras,
  onRecarregarConsultoras,
}: AbaTreinamentoProps) {
  const { aulas, salvarAulas, carregando } = useAulasConfig()

  const onSalvarAula = async (aulaEditada: Aula) => {
    const novas = aulas.map(a => (a.chave === aulaEditada.chave ? aulaEditada : a))
    await salvarAulas(novas)
  }

  // Admin vê gerenciamento + acompanhamento
  if (isAdmin) {
    return (
      <VisaoAdminTreinamento
        consultoras={consultoras}
        aulas={aulas}
        onSalvarAula={onSalvarAula}
      />
    )
  }

  // Consultora logada
  const consultora = useMemo(
    () => consultoras.find(c => c.usuario === consultoraLogada) || null,
    [consultoras, consultoraLogada]
  )

  // Regra de acesso: ativo (mensal/anual), gratuito (equipe) ou capacitacao
  const temAcesso = useMemo(() => {
    const status = consultora?.statusAssinatura
    return status === 'ativo' || status === 'gratuito' || status === 'capacitacao'
  }, [consultora])

  // Define se a consultora é "assinante paga" (plano anual/ativo) ou "gratuita"
  const ehAssinante = useMemo(() => {
    return consultora?.statusAssinatura === 'ativo'
  }, [consultora])

  // Aulas visíveis para esta consultora conforme status + visibilidade
  const aulasVisiveis = useMemo(() => {
    return aulas.filter(a => {
      if (!a.disponivel) return false
      if (a.visivelPara === 'ambos') return true
      if (a.visivelPara === 'assinantes') return ehAssinante
      if (a.visivelPara === 'gratuito') return !ehAssinante
      return true
    })
  }, [aulas, ehAssinante])

  const totalAulasVisiveis = aulasVisiveis.length

  const [progresso, setProgresso] = useState<ProgressoTreinamento>({})
  const [aulaAtiva, setAulaAtiva] = useState<Aula | null>(null)
  const [salvando, setSalvando] = useState(false)

  // Inicializa o progresso a partir da consultora carregada
  useEffect(() => {
    if (consultora?.progressoTreinamento) {
      setProgresso(consultora.progressoTreinamento)
    } else {
      setProgresso({})
    }
  }, [consultora])

  const totalAssistidas = useMemo(
    () => aulasVisiveis.filter(a => progresso[a.chave]).length,
    [progresso, aulasVisiveis]
  )

  // Próxima aula não assistida (para destaque automático)
  const proximaAulaNaoAssistida = useMemo(
    () => aulasVisiveis.find(a => !progresso[a.chave]) || null,
    [progresso, aulasVisiveis]
  )

  if (!temAcesso) {
    return <BloqueioAcesso />
  }

  const assistir = (aula: Aula) => {
    setAulaAtiva(aula)
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const avancarProxima = (aulaAtual: Aula) => {
    const idx = aulasVisiveis.findIndex(a => a.chave === aulaAtual.chave)
    const proxima = idx >= 0 ? aulasVisiveis[idx + 1] : null
    if (proxima) setAulaAtiva(proxima)
  }

  const marcarComoAssistida = async (aula: Aula) => {
    if (!consultora?._id) {
      toast.error('Não foi possível identificar sua conta.')
      return
    }
    if (progresso[aula.chave]) {
      // Já assistida — apenas avança
      avancarProxima(aula)
      return
    }
    setSalvando(true)
    try {
      const novoProgresso: ProgressoTreinamento = {
        ...progresso,
        [aula.chave]: true,
      }
      const novoTotal = aulasVisiveis.filter(a => novoProgresso[a.chave]).length
      novoProgresso.totalAssistidas = novoTotal
      if (!novoProgresso.dataInicioTreinamento) {
        novoProgresso.dataInicioTreinamento = new Date().toISOString()
      }
      novoProgresso.dataConclusao = novoTotal >= totalAulasVisiveis ? new Date().toISOString() : null

      await lumi.entities.consultoras.update(consultora._id, {
        progressoTreinamento: novoProgresso,
        updatedAt: new Date().toISOString(),
      })
      setProgresso(novoProgresso)
      toast.success(`Aula ${aula.num} marcada como assistida!`)
      if (onRecarregarConsultoras) onRecarregarConsultoras()
      avancarProxima(aula)
    } catch (e) {
      console.error('[treinamento] erro ao salvar progresso', e)
      toast.error('Erro ao salvar progresso. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  const idxAtiva = aulaAtiva ? aulasVisiveis.findIndex(a => a.chave === aulaAtiva.chave) : -1
  const proximaDoPlayer = idxAtiva >= 0 ? aulasVisiveis[idxAtiva + 1] || null : null

  return (
    <div>
      {/* Cabeçalho */}
      <div className="mb-6">
        <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro mb-1 flex items-center gap-2">
          <GraduationCap className="w-6 h-6 text-verde-escuro" />
          Como Usar o Sistema Descubra seus Óleos
        </h2>
        <p className="text-sm text-texto-suave">
          Assista às aulas e pratique direto no painel.
        </p>
      </div>

      {carregando ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-texto-suave">
          Carregando aulas...
        </div>
      ) : totalAulasVisiveis === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center text-texto-suave">
          Nenhuma aula disponível no momento.
        </div>
      ) : (
        <>
          {/* Barra de progresso */}
          <div className="bg-white rounded-xl shadow-md p-5 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-texto-escuro">
                {totalAssistidas} de {totalAulasVisiveis} aulas assistidas
              </span>
              <span className="text-xs text-texto-suave">
                {Math.round((totalAssistidas / totalAulasVisiveis) * 100)}%
              </span>
            </div>
            <div className="w-full h-3 rounded-full bg-bege-medio overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${(totalAssistidas / totalAulasVisiveis) * 100}%`, backgroundColor: '#2D6A4F' }}
              />
            </div>
          </div>

          {/* Separador visual */}
          <div className="border-t-2 mb-6" style={{ borderColor: '#A8DFC4' }} />

          {/* Player (no topo, quando há aula ativa) */}
          {aulaAtiva && (
            <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6 mb-8">
              <div
                className="sp-embed-player w-full overflow-hidden bg-black"
                style={{ borderRadius: 12, position: 'relative', width: '100%', paddingTop: '56.25%', height: 0 }}
              >
                <iframe
                  key={aulaAtiva.chave}
                  src={montarSrcScreenPal(aulaAtiva.spId)}
                  title={aulaAtiva.titulo}
                  scrolling="no"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  allowFullScreen
                />
              </div>
              <div className="mt-4">
                <div className="text-xs text-texto-suave mb-1">Aula {aulaAtiva.num}</div>
                <h3 className="text-lg font-cormorant font-semibold text-texto-escuro mb-4">
                  {aulaAtiva.titulo}
                </h3>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => marcarComoAssistida(aulaAtiva)}
                    disabled={salvando}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-white text-sm transition-all hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: '#2D6A4F' }}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {progresso[aulaAtiva.chave]
                      ? 'Aula assistida'
                      : 'Marcar como assistida'}
                  </button>
                  {proximaDoPlayer && (
                    <button
                      onClick={() => assistir(proximaDoPlayer)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-texto-suave text-sm border border-bege-medio hover:bg-bege-fundo transition-all"
                    >
                      Próxima aula
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lista de aulas */}
          <div className="space-y-3">
            {aulasVisiveis.map(aula => {
              const assistida = !!progresso[aula.chave]
              const ativaAgora = aulaAtiva?.chave === aula.chave
              const destaque = !assistida && proximaAulaNaoAssistida?.chave === aula.chave
              return (
                <div
                  key={aula.chave}
                  className={`bg-white rounded-xl shadow-md p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 transition-all ${
                    ativaAgora ? 'ring-2 ring-[#2D6A4F]' : destaque ? 'ring-2 ring-[#A8DFC4]' : ''
                  }`}
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm"
                    style={
                      assistida
                        ? { backgroundColor: '#D8F3DC', color: '#2D6A4F' }
                        : { backgroundColor: '#F8F4EF', color: '#5F5E5A' }
                    }
                  >
                    {assistida ? <CheckCircle2 className="w-5 h-5" /> : aula.num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-xs text-texto-suave">Aula {aula.num}</span>
                      {assistida && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                          style={{ backgroundColor: '#D8F3DC', color: '#2D6A4F' }}
                        >
                          ✅ Assistida
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-medium text-texto-escuro">{aula.titulo}</div>
                  </div>
                  <div className="flex-shrink-0">
                    {assistida ? (
                      <button
                        onClick={() => assistir(aula)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm border border-bege-medio text-texto-suave hover:bg-bege-fundo transition-all"
                      >
                        <Play className="w-4 h-4" />
                        Assistir novamente
                      </button>
                    ) : (
                      <button
                        onClick={() => assistir(aula)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm text-white transition-all hover:opacity-90"
                        style={{ backgroundColor: '#2D6A4F' }}
                      >
                        <Play className="w-4 h-4" />
                        Assistir aula
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
